import { GetStaticPaths, GetStaticProps } from 'next';
import { CollectionPageLayout } from '@/components';
import { TypeCollectionPage } from '@/core/generated-types';
import { defaultCacheRevalidation } from '@/core/contentfulConfig';
import getPathsFromEntries from '@/utils/pathBuilder';
import { CONTENT_TYPE_COLLECTION_PAGE } from '@/core/constants';
import { ShopifyProduct } from '@/lib/shopify';
import { loadLayout } from '@/utils/loadLayout';
import { ContentfulPreviewData, getEntriesByPageType } from '@/lib/contentful';
import { filterContentfulObject } from '@/utils/helpers';

export interface CollectionPageProps {
  error?: string;
  page: TypeCollectionPage;
  shopifyProducts: (ShopifyProduct | null)[];
}

const CollectionPage = ({
  error,
  page,
  shopifyProducts,
}: CollectionPageProps) => {
  if (error) {
    return (
      <div>
        <h1>An Error occurred: </h1>
        <h2>{error}</h2>
      </div>
    );
  }

  switch (page.sys.contentType.sys.id) {
    case CONTENT_TYPE_COLLECTION_PAGE.COLLECTION_PAGE:
      return (
        <CollectionPageLayout page={page} shopifyProducts={shopifyProducts} />
      );
    default:
      console.warn('No matching collection page layout');
      return null;
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  const pageTypes = CONTENT_TYPE_COLLECTION_PAGE;
  const getEntries = await getEntriesByPageType(
    {
      include: 1,
    },
    pageTypes,
  );
  const paths = getPathsFromEntries(getEntries);

  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps = async ({
  params,
  preview = false,
  previewData,
}) => {
  const locale = params?.locale as string;
  const previewDataPayload = previewData as ContentfulPreviewData;
  const { header, footer, footerLegal } = await loadLayout(
    locale,
    preview,
    previewDataPayload,
  );

  const result = await getEntriesByPageType(
    {
      'fields.slug': params?.slug as string,
      locale,
    },
    CONTENT_TYPE_COLLECTION_PAGE,
    preview,
    previewDataPayload,
  );

  const page = result?.[0].items?.[0] as TypeCollectionPage;

  return {
    notFound: !page,
    props: {
      page: filterContentfulObject(page),
      header,
      footer,
      footerLegal,
    },
    revalidate: defaultCacheRevalidation,
  };
};

export default CollectionPage;
